import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { ca } from "./translations/ca";
import { es } from "./translations/es";

const TRANSLATIONS = { ca, es };
const STORAGE_KEY = "aim_lang";

const LanguageContext = createContext({
  lang: "ca",
  setLang: () => {},
  t: (key) => key,
});

function detectLanguage() {
  if (typeof window === "undefined") return "ca";

  // 1) URL path prefix (/es/* → Spanish). Most authoritative for SEO.
  if (window.location.pathname.startsWith("/es")) return "es";

  // 2) URL param wins (?lang=)
  const params = new URLSearchParams(window.location.search);
  const fromUrl = (params.get("lang") || "").toLowerCase();
  if (fromUrl === "ca" || fromUrl === "es") return fromUrl;

  // 3) localStorage
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "ca" || saved === "es") return saved;

  // 4) Browser language
  const nav = (navigator.language || "ca").toLowerCase();
  if (nav.startsWith("ca")) return "ca";
  if (nav.startsWith("es")) return "es";

  // 5) Default
  return "ca";
}

/**
 * Resolve a dot-path key against the translation object.
 * Supports arrays via numeric segments: "hero.promises.0.k"
 */
function resolve(obj, path) {
  return path
    .split(".")
    .reduce((acc, seg) => (acc == null ? undefined : acc[seg]), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectLanguage);

  const setLang = useCallback((next) => {
    if (next !== "ca" && next !== "es") return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      console.warn("[i18n] no s'ha pogut desar l'idioma a localStorage:", err);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = next;
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const t = useCallback(
    (key, fallback) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS.ca;
      const val = resolve(dict, key);
      if (val == null) {
        // fallback to Catalan if missing in current locale
        const caVal = resolve(TRANSLATIONS.ca, key);
        return caVal != null ? caVal : fallback != null ? fallback : key;
      }
      return val;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
